import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useLang } from '../../../i18n';
import Card from '../../../components/Card';
import Badge from '../../../components/Badge';
import type { OrderStage, StageStatus } from '../../../types';

interface Props { stages: OrderStage[] }

function stageVariant(status: StageStatus): 'neutral' | 'brand' | 'success' | 'warning' | 'danger' {
  if (status === 'done') return 'success';
  if (status === 'in_progress') return 'brand';
  if (status === 'blocked') return 'danger';
  if (status === 'review') return 'warning';
  return 'neutral';
}

export default function OrderStages({ stages }: Props) {
  const { t } = useLang();
  const [open, setOpen] = useState<string | null>(null);
  if (stages.length === 0) {
    return <Card padding="lg"><p>—</p></Card>;
  }
  return (
    <div className="order-stages">
      {stages.map((s) => {
        const isOpen = open === s.public_id;
        return (
          <Card key={s.public_id} padding="md" className="order-stages__item">
            <button
              type="button"
              className="order-stages__head"
              onClick={() => setOpen(isOpen ? null : s.public_id)}
              aria-expanded={isOpen}
            >
              <span className="order-stages__head-icon" aria-hidden="true">
                {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </span>
              <span className="order-stages__head-title">{s.title}</span>
              <Badge variant={stageVariant(s.status)} size="sm">{s.status}</Badge>
            </button>
            {isOpen ? (
              <div className="order-stages__body">
                <p className="order-stages__desc">{s.description || '—'}</p>
                <dl className="order-stages__meta">
                  <div className="order-stages__row">
                    <dt>{t('order.stage.weight')}</dt>
                    <dd>{(s as any).weight_pct ? `${(s as any).weight_pct}%` : '—'}</dd>
                  </div>
                  <div className="order-stages__row">
                    <dt>{t('order.stage.deadline')}</dt>
                    <dd>{s.completed_at ? new Date(s.completed_at).toLocaleDateString() : '—'}</dd>
                  </div>
                  <div className="order-stages__row">
                    <dt>{t('order.stage.responsible')}</dt>
                    <dd>{s.assignee_id ? `#${s.assignee_id}` : '—'}</dd>
                  </div>
                </dl>
              </div>
            ) : null}
          </Card>
        );
      })}
    </div>
  );
}
